import React, { useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
} from "reactstrap";

import {
  DashboardLayoutPreset,
  DashboardWidgetId,
  DashboardWidgetLayoutItem,
  applyLayoutPreset,
  canCustomizeDashboard,
  mergeLayoutPreference,
} from "../../../helpers/dashboardLayoutCatalog";
import { getAuthRoleNames } from "../../../services/authService";
import {
  deleteDashboardLayout,
  putDashboardLayout,
} from "../../../services/dashboardLayoutService";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  widgets: DashboardWidgetLayoutItem[];
  onSaved: (widgets: DashboardWidgetLayoutItem[] | null) => void;
  /** Live preview on the dashboard while editing (reverted on cancel). */
  onPreviewChange?: (widgets: DashboardWidgetLayoutItem[] | null) => void;
};

const widgetLabelKey = (id: DashboardWidgetId): string => {
  switch (id) {
    case "insights":
      return "studio.dashboard.insights.title";
    case "kpi_clients":
      return "studio.dashboard.widgets.clients";
    case "kpi_professionals":
      return "studio.dashboard.widgets.professionals";
    case "kpi_appointments_today":
      return "studio.dashboard.widgets.appointmentsToday";
    case "kpi_orders_open":
      return "studio.dashboard.widgets.ordersOpen";
    case "kpi_packages_expiring":
      return "studio.dashboard.widgets.packagesExpiring";
    case "appointments_today":
      return "studio.dashboard.appointments.title";
    case "day_estimate":
      return "studio.dashboard.dayEstimate.title";
    case "pendings":
      return "studio.dashboard.pendings.title";
    case "quick_actions":
      return "studio.dashboard.quickActions.title";
    case "recent_improvements":
      return "studio.dashboard.improvements.title";
    default:
      return "studio.dashboard.customize.title";
  }
};

const DashboardCustomizeOffcanvas = ({
  isOpen,
  onClose,
  widgets,
  onSaved,
  onPreviewChange,
}: Props) => {
  const { t } = useTranslation();
  const roles = useMemo(() => getAuthRoleNames(), []);
  const allowed = canCustomizeDashboard(roles);
  const [draft, setDraft] = useState<DashboardWidgetLayoutItem[]>(widgets);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const next = mergeLayoutPreference(widgets, roles);
      setDraft(next);
      onPreviewChange?.(next);
    } else {
      onPreviewChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preview only when opening
  }, [isOpen, widgets, roles]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    onPreviewChange?.(draft);
  }, [draft, isOpen, onPreviewChange]);

  if (!allowed) {
    return null;
  }

  const visibleCount = draft.filter((item) => item.visible).length;
  const previewVisible = draft
    .filter((item) => item.visible)
    .sort((a, b) => a.order - b.order);

  const updateDraft = (
    updater: (current: DashboardWidgetLayoutItem[]) => DashboardWidgetLayoutItem[]
  ) => {
    setDraft((current) => updater(current));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    updateDraft((current) => {
      const next = [...current];
      const [row] = next.splice(result.source.index, 1);
      next.splice(result.destination!.index, 0, row);
      return next.map((item, order) => ({ ...item, order }));
    });
  };

  const moveWithKeyboard = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= draft.length) {
      return;
    }
    updateDraft((current) => {
      const next = [...current];
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      return next.map((item, order) => ({ ...item, order }));
    });
  };

  const toggleVisible = (id: DashboardWidgetId) => {
    updateDraft((current) => {
      const next = current.map((item) =>
        item.id === id ? { ...item, visible: !item.visible } : item
      );
      if (!next.some((item) => item.visible)) {
        toast.info(t("studio.dashboard.customize.minOne"));
        return current;
      }
      return next;
    });
  };

  const applyPreset = (preset: DashboardLayoutPreset) => {
    setDraft(applyLayoutPreset(preset, roles));
    toast.info(
      preset === "balcao"
        ? t("studio.dashboard.customize.presetBalcaoApplied")
        : t("studio.dashboard.customize.presetDonoApplied")
    );
  };

  const handleClose = () => {
    onPreviewChange?.(null);
    onClose();
  };

  const handleSave = async () => {
    if (visibleCount < 1) {
      toast.info(t("studio.dashboard.customize.minOne"));
      return;
    }

    setSaving(true);
    try {
      const response = await putDashboardLayout(draft);
      const saved = mergeLayoutPreference(response.data?.widgets ?? draft, roles);
      onSaved(saved);
      onPreviewChange?.(null);
      toast.success(response.message || t("studio.dashboard.customize.saved"));
      onClose();
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("studio.dashboard.customize.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const confirmation = await Swal.fire({
      title: t("studio.dashboard.customize.resetConfirmTitle"),
      text: t("studio.dashboard.customize.resetConfirmBody"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("studio.dashboard.customize.reset"),
      cancelButtonText: t("studio.dashboard.customize.cancel"),
      confirmButtonColor: "#0ab39c",
      cancelButtonColor: "#878a99",
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setResetting(true);
    try {
      const response = await deleteDashboardLayout();
      onSaved(null);
      onPreviewChange?.(null);
      toast.success(response.message || t("studio.dashboard.customize.resetSuccess"));
      onClose();
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("studio.dashboard.customize.saveError"));
    } finally {
      setResetting(false);
    }
  };

  return (
    <Offcanvas direction="end" isOpen={isOpen} toggle={handleClose}>
      <OffcanvasHeader toggle={handleClose}>
        {t("studio.dashboard.customize.title")}
      </OffcanvasHeader>
      <OffcanvasBody>
        <p className="text-muted small">{t("studio.dashboard.customize.hint")}</p>
        <p className="text-muted small mb-2">
          {t("studio.dashboard.customize.dragHint")}
        </p>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Button
            color="soft-primary"
            size="sm"
            type="button"
            disabled={saving || resetting}
            onClick={() => applyPreset("balcao")}
          >
            {t("studio.dashboard.customize.presetBalcao")}
          </Button>
          <Button
            color="soft-success"
            size="sm"
            type="button"
            disabled={saving || resetting}
            onClick={() => applyPreset("dono")}
          >
            {t("studio.dashboard.customize.presetDono")}
          </Button>
        </div>

        <div className="border rounded bg-light p-2 mb-3">
          <div className="small text-muted mb-2">
            {t("studio.dashboard.customize.previewTitle")}
          </div>
          {previewVisible.length === 0 ? (
            <span className="small text-muted">
              {t("studio.dashboard.customize.previewEmpty")}
            </span>
          ) : (
            <ol className="small mb-0 ps-3">
              {previewVisible.map((item) => (
                <li key={item.id} className="mb-1">
                  {t(widgetLabelKey(item.id))}
                </li>
              ))}
            </ol>
          )}
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="dashboard-widgets">
            {(provided) => (
              <div
                className="d-flex flex-column gap-2 mb-4"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {draft.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className={`border rounded p-2 d-flex align-items-center justify-content-between gap-2 ${
                          snapshot.isDragging ? "shadow-sm bg-light" : "bg-white"
                        }`}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost-secondary btn-sm px-1"
                          {...dragProvided.dragHandleProps}
                          title={t("studio.dashboard.customize.dragHandle")}
                          aria-label={t("studio.dashboard.customize.dragHandle")}
                          disabled={saving || resetting}
                        >
                          <i className="ri-draggable" aria-hidden="true" />
                        </button>
                        <FormGroup check className="mb-0 flex-grow-1">
                          <Input
                            type="checkbox"
                            id={`dash-widget-${item.id}`}
                            checked={item.visible}
                            onChange={() => toggleVisible(item.id)}
                            disabled={saving || resetting}
                          />
                          <Label
                            check
                            htmlFor={`dash-widget-${item.id}`}
                            className="ms-1"
                          >
                            {t(widgetLabelKey(item.id))}
                          </Label>
                        </FormGroup>
                        <div className="btn-group btn-group-sm">
                          <Button
                            color="light"
                            type="button"
                            disabled={index === 0 || saving || resetting}
                            title={t("studio.dashboard.customize.moveUp")}
                            aria-label={t("studio.dashboard.customize.moveUp")}
                            onClick={() => moveWithKeyboard(index, -1)}
                          >
                            <i className="ri-arrow-up-s-line" aria-hidden="true" />
                          </Button>
                          <Button
                            color="light"
                            type="button"
                            disabled={
                              index === draft.length - 1 || saving || resetting
                            }
                            title={t("studio.dashboard.customize.moveDown")}
                            aria-label={t("studio.dashboard.customize.moveDown")}
                            onClick={() => moveWithKeyboard(index, 1)}
                          >
                            <i
                              className="ri-arrow-down-s-line"
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="d-grid gap-2">
          <Button
            color="primary"
            type="button"
            disabled={saving || resetting || visibleCount < 1}
            onClick={() => void handleSave()}
          >
            {saving ? <Spinner size="sm" className="me-1" /> : null}
            {t("studio.dashboard.customize.save")}
          </Button>
          <Button
            color="soft-secondary"
            type="button"
            disabled={saving || resetting}
            onClick={() => void handleReset()}
          >
            {resetting ? <Spinner size="sm" className="me-1" /> : null}
            {t("studio.dashboard.customize.reset")}
          </Button>
        </div>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default DashboardCustomizeOffcanvas;
